import type { PhaseConfig } from 'boardgame.io';
import { debug } from '../../utils/debug';
import type { DetroitGameState } from '../../types';
import { computeTotalExpenses } from '../../types';
import { DETROIT_RULES } from '../../../data/map/detroit_bankruptcy_solo/config';
import { undoToTurnStart } from '../moves/undoToTurnStart';

/**
 * Phase 7: 費用支払フェーズ（Detroit固有）
 *
 * 各プレイヤーは経費（株式配当 + 機関車維持費 + ターン数）を自動的に支払います。
 * ロジック処理後、ユーザーがクリックするまで待機します。
 *
 * 処理内容:
 * - 株式配当: sharesIssued × $1
 * - 機関車維持費: LOCOMOTIVE_COSTS[locomotiveLevel]
 * - ターン数コスト: actualTurn × $1（Detroit固有）
 * - 合計経費を現金から支払う
 * - 現金不足の場合、収入レベルを減少
 * - 収入レベルが負になった場合、倒産
 * - 倒産プレイヤーは処理をスキップ
 *
 * 参照:
 * - docs/specifications/logic/detroit_bankruptcy_solo/phase_07_pay_expenses_override.md
 */
export const payExpensesDetroitPhase: PhaseConfig<DetroitGameState> = {
  // Phase開始時に自動実行
  onBegin: ({ G, ctx }: { G: DetroitGameState; ctx: any }) => {
    debug.log('[payExpensesDetroitPhase] ★★★ Phase 7: Pay Expenses started ★★★');
    debug.log('[payExpensesDetroitPhase] ★★★ ctx:', { turn: ctx.turn, phase: ctx.phase, currentPlayer: ctx.currentPlayer });

    // ソロプレイ用: Player 0の経費支払
    const player = G.players['0'];
    if (!player || player.isBankrupt) {
      // 倒産時は即座に次フェーズへ（結果表示なし）
      G.autoPhaseResult = null;
      return;
    }

    // 経費計算（共通関数）
    const totalExpense = computeTotalExpenses(G);

    debug.log(`[payExpensesDetroitPhase] Player 0 expenses: $${totalExpense}`);

    // 支払処理
    if (player.cash >= totalExpense) {
      // 現金が十分な場合
      player.cash -= totalExpense;
      debug.log(`[payExpensesDetroitPhase] Player 0 paid $${totalExpense}, remaining cash: $${player.cash}`);
    } else {
      // 現金が不足する場合
      const shortage = totalExpense - player.cash;
      player.cash = 0;
      player.incomeLevel -= shortage;

      debug.log(`[payExpensesDetroitPhase] Player 0 insufficient cash, shortage: $${shortage}, incomeLevel: ${player.incomeLevel}`);

      // 収入レベルが負になった場合は破産
      if (player.incomeLevel < 0) {
        player.isBankrupt = true;
        G.isGameOver = true;
        G.endReason = 'BANKRUPTCY';

        // 勝敗判定（Detroit固有: ターン7以上で破産なら勝利）
        player.finalScore = G.actualTurn;
        player.isVictorious = G.actualTurn >= DETROIT_RULES.victoryTurnThreshold;

        debug.log(`[payExpensesDetroitPhase] ★ Player BANKRUPT at turn ${G.actualTurn}`);
        debug.log(`[payExpensesDetroitPhase] ★ Victory: ${player.isVictorious ? 'YES' : 'NO'}`);

        // 破産の処理結果を保存
        G.autoPhaseResult = {
          phase: 'payExpenses',
          message: player.isVictorious
            ? `VICTORY! Survived ${G.actualTurn} turns!`
            : `DEFEAT. Bankrupt on turn ${G.actualTurn}.`,
          floatingText: player.isVictorious ? `VICTORY!` : `BANKRUPT!`,
          details: {
            expenses: totalExpense,
            bankrupt: true
          },
        };
        return;
      }
    }

    // 処理結果を保存（UI表示用）
    G.autoPhaseResult = {
      phase: 'payExpenses',
      message: 'Paying expenses...',
      floatingText: `-$${totalExpense}`,
      details: {
        expenses: totalExpense,
      },
    };

    debug.log('[payExpensesDetroitPhase] ★ Auto-processing complete, waiting for user click');
  },

  moves: {
    // ユーザークリックでフェーズ進行
    proceedAutoPhase: ({ G, events }: { G: DetroitGameState; events: any }) => {
      // Phase内でのみ呼び出し可能（Boardgame.ioが保証）
      G.autoPhaseResult = null;
      events.endPhase();
    },
    undoToTurnStart,
  },

  // 倒産プレイヤーは自動的に次フェーズへ、または Undo 強制遷移
  endIf: ({ G }: { G: DetroitGameState }) => {
    // Undo 強制遷移フラグが立っている場合は即座に終了
    if (G.forcePhaseToIssueShares) {
      debug.log('[payExpensesDetroitPhase] Force transition to issueShares (Undo)');
      return true;
    }

    const player = G.players['0'];
    if (!player || player.isBankrupt) {
      return true;
    }
    return false;
  },

  onEnd: ({ G, ctx }: { G: DetroitGameState; ctx: any }) => {
    debug.log('[payExpensesDetroitPhase] ★★★ onEnd called - Phase 7 ended ★★★');
    debug.log('[payExpensesDetroitPhase] ★★★ ctx:', { turn: ctx.turn, phase: ctx.phase });
    debug.log('[payExpensesDetroitPhase] ★★★ Next phase should be: incomeReduction');

    // 自動フェーズ履歴に追加（UIアニメーション用）
    if (!G.completedAutoPhases) {
      G.completedAutoPhases = [];
    }
    G.completedAutoPhases.push('payExpenses');
  },

  // Phase 8 へ遷移、または Undo 時は issueShares へ
  next: ({ G }: { G: DetroitGameState }) => {
    if (G.forcePhaseToIssueShares) {
      return 'issueShares';
    }
    return 'incomeReduction';
  }
};
